import { useCallback, useEffect, useMemo, useState } from "react";
import {
    AlertTriangle,
    Activity,
    Clock,
    Database,
    RefreshCw,
    Search,
    ServerCrash,
    X
} from "lucide-react";
import {
    getRequestLogDetail,
    getRequestLogs,
    getSystemLogSummary
} from "../services/systemLogsService";
import "./SystemLogsPage.css";
import PageShell from "../components/layout/PageShell";

export default function SystemLogsPage() {
    const [summary, setSummary] = useState(null);
    const [logs, setLogs] = useState([]);
    const [selected, setSelected] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [loading, setLoading] = useState(true);

    const [filters, setFilters] = useState({
        level: "All",
        method: "All",
        path: "",
        take: 100
    });

    const load = useCallback(async () => {
        setLoading(true);

        try {
            const [summaryResult, logsResult] = await Promise.all([
                getSystemLogSummary(),
                getRequestLogs(filters)
            ]);

            setSummary(summaryResult);
            setLogs(logsResult?.items || []);
        } finally {
            setLoading(false);
        }
    }, [filters]);

    useEffect(() => {
        load();
    }, [load]);

    const openDetail = async (id) => {
        setDetailLoading(true);

        try {
            const result = await getRequestLogDetail(id);
            setSelected(result?.request || null);
        } finally {
            setDetailLoading(false);
        }
    };

    const cards = useMemo(() => {
        return [
            {
                label: "Requests",
                value: summary?.total ?? 0,
                icon: Activity
            },
            {
                label: "Errors",
                value: summary?.errors ?? 0,
                icon: ServerCrash
            },
            {
                label: "Warnings",
                value: summary?.warnings ?? 0,
                icon: AlertTriangle
            },
            {
                label: "Slow APIs",
                value: summary?.slow ?? 0,
                icon: Clock
            }
        ];
    }, [summary]);

    return (
        <PageShell title="SystemLogs">
            <div className="syslogs-page">
                <section className="syslogs-hero">
                    <div>
                        <span className="syslogs-kicker">Platform observability</span>
                        <h1>System Logs</h1>
                        <p>
                            Track API requests, failures, timings, and SQL queries from one SuperAdmin screen.
                        </p>
                    </div>

                    <button type="button" className="syslogs-refresh" onClick={load}>
                        <RefreshCw size={17} />
                        Refresh
                    </button>
                </section>

                <section className="syslogs-cards">
                    {cards.map((card) => {
                        const Icon = card.icon;

                        return (
                            <article key={card.label} className="syslogs-card">
                                <span>
                                    <Icon size={18} />
                                </span>
                                <strong>{card.value}</strong>
                                <small>{card.label}</small>
                            </article>
                        );
                    })}
                </section>

                <section className="syslogs-toolbar">
                    <div className="syslogs-search">
                        <Search size={16} />
                        <input
                            value={filters.path}
                            onChange={(e) =>
                                setFilters((current) => ({
                                    ...current,
                                    path: e.target.value
                                }))
                            }
                            placeholder="Search path, e.g. preview-session, crm, dashboard"
                        />
                    </div>

                    <select
                        value={filters.level}
                        onChange={(e) =>
                            setFilters((current) => ({
                                ...current,
                                level: e.target.value
                            }))
                        }
                    >
                        <option value="All">All levels</option>
                        <option value="Error">Errors</option>
                        <option value="Warning">Warnings</option>
                        <option value="Info">Info</option>
                    </select>

                    <select
                        value={filters.method}
                        onChange={(e) =>
                            setFilters((current) => ({
                                ...current,
                                method: e.target.value
                            }))
                        }
                    >
                        <option value="All">All methods</option>
                        <option value="GET">GET</option>
                        <option value="POST">POST</option>
                        <option value="PUT">PUT</option>
                        <option value="PATCH">PATCH</option>
                        <option value="DELETE">DELETE</option>
                    </select>
                </section>

                <section className="syslogs-table-card">
                    <div className="syslogs-table-head">
                        <strong>Latest requests</strong>
                        <span>{loading ? "Loading..." : `${logs.length} loaded`}</span>
                    </div>

                    <div className="syslogs-table-wrap">
                        <table className="syslogs-table">
                            <thead>
                                <tr>
                                    <th>Time</th>
                                    <th>Status</th>
                                    <th>Method</th>
                                    <th>Path</th>
                                    <th>API</th>
                                    <th>SQL</th>
                                    <th>User</th>
                                </tr>
                            </thead>

                            <tbody>
                                {logs.map((log) => (
                                    <tr key={log.id} onClick={() => openDetail(log.id)}>
                                        <td>{formatDate(log.createdAtUtc)}</td>
                                        <td>
                                            <span className={`syslogs-status ${statusClass(log.statusCode)}`}>
                                                {log.statusCode}
                                            </span>
                                        </td>
                                        <td>
                                            <span className="syslogs-method">{log.method}</span>
                                        </td>
                                        <td className="syslogs-path">
                                            {log.path}
                                            {log.queryString ? <small>{log.queryString}</small> : null}
                                            {log.exceptionMessage ? (
                                                <em>{log.exceptionMessage}</em>
                                            ) : null}
                                        </td>
                                        <td>{log.durationMs}ms</td>
                                        <td>
                                            <span className="syslogs-sql-pill">
                                                <Database size={13} />
                                                {log.sqlQueryCount} / {log.sqlTotalDurationMs}ms
                                            </span>
                                        </td>
                                        <td>{log.userEmail || "-"}</td>
                                    </tr>
                                ))}

                                {!loading && logs.length === 0 ? (
                                    <tr>
                                        <td colSpan="7" className="syslogs-empty">
                                            No logs found for selected filters.
                                        </td>
                                    </tr>
                                ) : null}
                            </tbody>
                        </table>
                    </div>
                </section>

                {selected ? (
                    <aside className="syslogs-drawer">
                        <div className="syslogs-drawer-head">
                            <div>
                                <strong>{selected.method} {selected.path}</strong>
                                <span>{selected.traceId}</span>
                            </div>

                            <button type="button" onClick={() => setSelected(null)}>
                                <X size={18} />
                            </button>
                        </div>

                        {detailLoading ? (
                            <div className="syslogs-drawer-loading">Loading details...</div>
                        ) : (
                            <div className="syslogs-drawer-body">
                                <div className="syslogs-detail-grid">
                                    <Info label="Status" value={selected.statusCode} />
                                    <Info label="Duration" value={`${selected.durationMs}ms`} />
                                    <Info label="Level" value={selected.logLevel} />
                                    <Info label="SQL queries" value={selected.sqlQueryCount} />
                                    <Info label="SQL total" value={`${selected.sqlTotalDurationMs}ms`} />
                                    <Info label="User" value={selected.userEmail || "-"} />
                                </div>

                                {selected.exceptionMessage ? (
                                    <Block title="Exception" value={selected.exceptionMessage} danger />
                                ) : null}

                                {selected.requestBodyPreview ? (
                                    <Block title="Request body" value={selected.requestBodyPreview} />
                                ) : null}

                                {selected.responseBodyPreview ? (
                                    <Block title="Response body" value={selected.responseBodyPreview} />
                                ) : null}

                                {selected.exceptionStackTrace ? (
                                    <Block title="Stack trace" value={selected.exceptionStackTrace} danger />
                                ) : null}

                                <h3>SQL queries</h3>

                                <div className="syslogs-sql-list">
                                    {(selected.sqlQueries || []).map((query) => (
                                        <article key={query.id} className="syslogs-sql-item">
                                            <div>
                                                <strong>{query.commandType}</strong>
                                                <span>{query.durationMs}ms</span>
                                            </div>

                                            {!query.success && query.errorMessage ? (
                                                <p className="syslogs-sql-error">{query.errorMessage}</p>
                                            ) : null}

                                            <pre>{query.commandTextPreview}</pre>

                                            {query.parametersJson ? (
                                                <pre>{query.parametersJson}</pre>
                                            ) : null}
                                        </article>
                                    ))}

                                    {(selected.sqlQueries || []).length === 0 ? (
                                        <p className="syslogs-muted">No SQL query captured for this request.</p>
                                    ) : null}
                                </div>
                            </div>
                        )}
                    </aside>
                ) : null}
            </div>
        </PageShell>

    );
}

function Info({ label, value }) {
    return (
        <div className="syslogs-info">
            <span>{label}</span>
            <strong>{value}</strong>
        </div>
    );
}

function Block({ title, value, danger = false }) {
    return (
        <section className={`syslogs-block ${danger ? "danger" : ""}`}>
            <strong>{title}</strong>
            <pre>{value}</pre>
        </section>
    );
}

function formatDate(value) {
    if (!value) return "-";

    try {
        return new Date(value).toLocaleString();
    } catch {
        return value;
    }
}

function statusClass(status) {
    if (status >= 500) return "error";
    if (status >= 400) return "warning";
    if (status >= 300) return "redirect";
    return "success";
}